/**
 * ActionEditor — Panel de edición de una acción DSL.
 * Se presenta como un modal deslizable desde el lateral.
 * Campos dinámicos según el tipo de acción seleccionada.
 */
import React, { useState, useEffect } from 'react';
import type { DSLAction, ActionType, SelectorStrategy, OnFailure } from '../../types';

const STRATEGIES: SelectorStrategy[] = ['css', 'xpath', 'text', 'testid', 'role', 'label'];
const ON_FAILURE_OPTIONS: OnFailure[] = ['abort', 'retry', 'skip'];

// Parámetros requeridos por tipo de acción
const PARAMS_CONFIG: Record<string, { key: string; label: string; type: string; placeholder?: string }[]> = {
  NAVIGATE:     [{ key: 'url', label: 'URL', type: 'text', placeholder: '{{base_url}}/ruta' }, { key: 'wait_until', label: 'Esperar hasta', type: 'select' }],
  INPUT:        [{ key: 'value', label: 'Valor', type: 'text', placeholder: 'Texto a escribir o {{variable}}' }, { key: 'delay_ms', label: 'Delay entre teclas (ms)', type: 'number' }],
  SELECT:       [{ key: 'value', label: 'Por valor', type: 'text' }, { key: 'label', label: 'Por texto visible', type: 'text' }, { key: 'index', label: 'Por índice', type: 'number' }],
  WAIT:         [{ key: 'duration_ms', label: 'Duración (ms)', type: 'number' }],
  WAIT_FOR:     [{ key: 'state', label: 'Estado esperado', type: 'select' }],
  WAIT_FOR_URL: [{ key: 'url_pattern', label: 'Patrón URL (glob)', type: 'text', placeholder: '**/dashboard/**' }],
  ASSERT_TEXT:  [{ key: 'expected', label: 'Texto esperado', type: 'text' }, { key: 'operator', label: 'Operador', type: 'select' }],
  ASSERT_VALUE: [{ key: 'expected', label: 'Valor esperado', type: 'text' }, { key: 'operator', label: 'Operador', type: 'select' }],
  ASSERT_URL:   [{ key: 'expected', label: 'URL esperada', type: 'text' }, { key: 'operator', label: 'Operador', type: 'select' }],
  ASSERT_TITLE: [{ key: 'expected', label: 'Título esperado', type: 'text' }, { key: 'operator', label: 'Operador', type: 'select' }],
  ASSERT_COUNT: [{ key: 'expected', label: 'Cantidad esperada', type: 'number' }],
  ASSERT_ATTRIBUTE: [{ key: 'attribute', label: 'Atributo', type: 'text', placeholder: 'href, class, value' }, { key: 'expected', label: 'Valor esperado', type: 'text' }],
  SCROLL:       [{ key: 'direction', label: 'Dirección', type: 'select' }, { key: 'amount', label: 'Píxeles', type: 'number' }],
  KEY_PRESS:    [{ key: 'key', label: 'Tecla', type: 'text', placeholder: 'Enter, Tab, Escape, ArrowDown' }],
  EXECUTE_SCRIPT: [{ key: 'script', label: 'JavaScript', type: 'textarea', placeholder: 'return document.title;' }, { key: 'result_variable', label: 'Guardar en variable', type: 'text' }],
  SET_VARIABLE: [{ key: 'variable_name', label: 'Nombre de variable', type: 'text' }, { key: 'value', label: 'Valor', type: 'text' }],
  HTTP_REQUEST: [{ key: 'method', label: 'Método', type: 'select' }, { key: 'url', label: 'URL', type: 'text' }, { key: 'expected_status', label: 'Status esperado', type: 'number' }],
  SCREENSHOT:   [{ key: 'name', label: 'Nombre del archivo', type: 'text' }, { key: 'full_page', label: 'Página completa', type: 'checkbox' }],
  UI_EXTRACT:   [{ key: 'attribute', label: 'Qué extraer', type: 'select', placeholder: 'textContent' }, { key: 'extract_to_column', label: 'Guardar en columna del dataset', type: 'text', placeholder: 'Nombre de columna (opcional)' }],
};

const SELECT_OPTIONS: Record<string, string[]> = {
  state:       ['visible', 'hidden', 'attached', 'detached'],
  operator:    ['equals', 'contains', 'starts_with', 'ends_with', 'matches', 'not_equals'],
  direction:   ['down', 'up', 'left', 'right'],
  wait_until:  ['load', 'domcontentloaded', 'networkidle'],
  method:      ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
  attribute:   ['textContent', 'value', 'innerText', 'href', 'src', 'class', 'disabled', 'checked'],
};

interface ActionEditorProps {
  action: DSLAction;
  onSave: (updated: DSLAction) => void;
  onClose: () => void;
  versionId?: string;
  datasetColumns?: string[];
}

export function ActionEditor({ action, onSave, onClose, versionId, datasetColumns }: ActionEditorProps) {
  // POM state
  const [pomMode, setPomMode] = useState<'pom' | 'manual'>(action.page_object_id ? 'pom' : 'manual');
  const [pomObjects, setPomObjects] = useState<{id: string; name: string; page_name: string; selector_primary: string; selector_fallbacks: string[]; selector_strategy: string; element_type: string}[]>([]);
  const [loadingPom, setLoadingPom] = useState(false);
  const [selectedPomId, setSelectedPomId] = useState<string>(action.page_object_id ?? '');

  const loadPomObjects = React.useCallback(async () => {
    if (!versionId) return;
    setLoadingPom(true);
    try {
      const BASE = '/api/v1';
      const token = localStorage.getItem('access_token') || '';
      const pages = await fetch(`${BASE}/versions/${versionId}/pages`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json());
      const all: typeof pomObjects = [];
      for (const page of pages) {
        const objs = await fetch(`${BASE}/pages/${page.id}/objects`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json());
        objs.forEach((o: Record<string, unknown>) => all.push({ ...o as typeof all[0], page_name: page.name as string }));
      }
      setPomObjects(all);
    } catch { }
    finally { setLoadingPom(false); }
  }, [versionId]);

  React.useEffect(() => {
    if (pomMode === 'pom' && versionId) loadPomObjects();
  }, [pomMode, versionId, loadPomObjects]);

  const applyPomObject = (objId: string) => {
    const obj = pomObjects.find(o => o.id === objId);
    if (!obj) return;
    setSelectedPomId(objId);
    setLocal(p => ({
      ...p,
      page_object_id: objId,
      target: {
        ...p.target,
        primary: obj.selector_primary,
        fallbacks: obj.selector_fallbacks ?? [],
        strategy: (obj.selector_strategy ?? 'css') as import('../../types').SelectorStrategy,
      },
    }));
  };

  const switchToManual = () => {
    setPomMode('manual');
    setLocal(p => ({ ...p, page_object_id: undefined }));
    setSelectedPomId('');
  };

  const switchToPom = () => {
    setPomMode('pom');
    if (!pomObjects.length && versionId) loadPomObjects();
  };

  const [local, setLocal] = useState<DSLAction>(() => {
    const a = JSON.parse(JSON.stringify(action));
    // Normalize: backend may return action_type instead of type
    if (!a.type && (a as Record<string,unknown>).action_type) {
      a.type = (a as Record<string,unknown>).action_type;
    }
    // Normalize: backend returns id instead of action_id
    if (!a.action_id && (a as Record<string,unknown>).id) {
      a.action_id = (a as Record<string,unknown>).id;
    }
    return a;
  });
  const [activeTab, setActiveTab] = useState<'selector' | 'params' | 'meta'>('selector');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const paramDefs = PARAMS_CONFIG[local.type] ?? [];
  const params = local.params as Record<string, unknown>;

  const updateParam = (key: string, value: unknown) => {
    setLocal(prev => ({ ...prev, params: { ...prev.params as Record<string,unknown>, [key]: value } }));
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    // ── Selector ─────────────────────────────────────────────────────────────
    const NO_TARGET = ['NAVIGATE', 'BACK', 'FORWARD', 'REFRESH', 'WAIT',
      'ASSERT_URL', 'ASSERT_TITLE', 'WAIT_FOR_URL', 'KEY_COMBO',
      'EXECUTE_SCRIPT', 'HTTP_REQUEST', 'CLOSE_TAB', 'WAIT_FOR_NETWORK',
      'SCREENSHOT', 'WAIT_FOR_NETWORK'];
    const needsTarget = !NO_TARGET.includes(local.type);
    if (needsTarget && pomMode === 'manual' && !local.target.primary.trim()) {
      errs.primary = 'El selector primario es requerido';
    }
    if (needsTarget && pomMode === 'pom' && !selectedPomId) {
      errs.primary = 'Selecciona un objeto del POM';
    }

    // ── Params por tipo de acción ─────────────────────────────────────────────
    const str = (k: string) => ((params[k] as string) ?? '').trim();
    const num = (k: string) => params[k] as number;

    switch (local.type) {
      case 'NAVIGATE':
        if (!str('url')) errs.url = 'La URL es requerida';
        break;
      case 'INPUT':
        if (str('value') === '') errs.value = 'El valor a ingresar es requerido';
        break;
      case 'SELECT':
        if (!str('value') && !str('label') && num('index') === undefined)
          errs.value = 'Ingresa valor, texto visible o índice';
        break;
      case 'WAIT':
        if (!num('duration_ms') || num('duration_ms') <= 0)
          errs.duration_ms = 'La duración debe ser mayor a 0';
        break;
      case 'WAIT_FOR_URL':
        if (!str('url_pattern')) errs.url_pattern = 'El patrón de URL es requerido';
        break;
      case 'ASSERT_TEXT':
        if (!str('expected')) errs.expected = 'El texto esperado es requerido';
        break;
      case 'ASSERT_VALUE':
        if (!str('expected')) errs.expected = 'El valor esperado es requerido';
        break;
      case 'ASSERT_URL':
        if (!str('expected')) errs.expected = 'La URL esperada es requerida';
        break;
      case 'ASSERT_TITLE':
        if (!str('expected')) errs.expected = 'El título esperado es requerido';
        break;
      case 'ASSERT_COUNT':
        if (num('expected') === undefined || num('expected') < 0)
          errs.expected = 'La cantidad esperada es requerida';
        break;
      case 'ASSERT_ATTRIBUTE':
        if (!str('attribute')) errs.attribute = 'El nombre del atributo es requerido';
        if (!str('expected')) errs.expected = 'El valor esperado es requerido';
        break;
      case 'KEY_PRESS':
        if (!str('key')) errs.key = 'La tecla es requerida (ej: Enter, Tab, Escape)';
        break;
      case 'EXECUTE_SCRIPT':
        if (!str('script')) errs.script = 'El script JavaScript es requerido';
        break;
      case 'UPLOAD_FILE':
        if (!str('file_path')) errs.file_path = 'La ruta del archivo es requerida';
        break;
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (validate()) onSave(local);
  };

  const TAB_STYLE = (active: boolean): React.CSSProperties => ({
    padding: '6px 14px', borderRadius: 8,
    border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
    background: active ? 'var(--accent-soft)' : 'transparent',
    color: active ? 'var(--accent)' : 'var(--color-text-secondary)',
    transition: 'all 0.15s',
  });

  return (
    // Backdrop
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        width: 560, maxHeight: '85vh', borderRadius: 16,
        background: 'var(--color-background-primary)',
        border: '1px solid var(--color-border-secondary)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--color-border-tertiary)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{
            padding: '3px 10px', borderRadius: 6,
            background: '#3B8BD422', color: '#3B8BD4',
            fontSize: 11, fontWeight: 700,
          }}>
            {local.type}
          </span>
          <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: 'var(--color-text-primary)' }}>
            Editar acción
          </span>
          <button onClick={onClose} style={{
            border: 'none', background: 'transparent',
            fontSize: 18, cursor: 'pointer',
            color: 'var(--color-text-tertiary)',
          }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ padding: '12px 20px 0', display: 'flex', gap: 4 }}>
          {(['selector', 'params', 'meta'] as const).map(tab => (
            <button key={tab} style={TAB_STYLE(activeTab === tab)} onClick={() => setActiveTab(tab)}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

          {activeTab === 'selector' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            {/* POM / Manual toggle */}
              {versionId && (
                <div style={{ display: 'flex', gap: 0, marginBottom: 4, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--color-border-secondary)', width: 'fit-content' }}>
                  <button onClick={switchToPom}
                    style={{ padding: '5px 14px', border: 'none', background: pomMode === 'pom' ? '#534AB7' : 'transparent', color: pomMode === 'pom' ? '#fff' : 'var(--color-text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    ◈ Del POM
                  </button>
                  <button onClick={switchToManual}
                    style={{ padding: '5px 14px', border: 'none', background: pomMode === 'manual' ? '#3B8BD4' : 'transparent', color: pomMode === 'manual' ? '#fff' : 'var(--color-text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    ✎ Manual
                  </button>
                </div>
              )}

              {pomMode === 'pom' && versionId ? (
                <Field label="Objeto del POM" error={errors.primary}>
                  {loadingPom ? (
                    <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--color-text-tertiary)' }}>Cargando objetos...</div>
                  ) : pomObjects.length === 0 ? (
                    <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--color-text-tertiary)', borderRadius: 8, border: '1px dashed var(--color-border-secondary)' }}>
                      Sin objetos POM — agrégalos en la sección ◈ POM
                    </div>
                  ) : (
                    <select
                      value={selectedPomId}
                      onChange={e => applyPomObject(e.target.value)}
                      style={{ ...INPUT_STYLE, color: selectedPomId ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)' }}>
                      <option value="">— Selecciona un objeto —</option>
                      {Array.from(new Set(pomObjects.map(o => o.page_name))).map(pageName => (
                        <optgroup key={pageName} label={pageName}>
                          {pomObjects.filter(o => o.page_name === pageName).map(obj => (
                            <option key={obj.id} value={obj.id}>
                              {obj.name} — {obj.selector_primary}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  )}
                  {selectedPomId && (
                    <div style={{ marginTop: 6, padding: '6px 10px', borderRadius: 6, background: '#EEEDFE', border: '1px solid #C4C0F5', fontSize: 11, color: '#534AB7' }}>
                      ◈ Vinculado al POM — el selector se actualiza automáticamente si cambia en el POM
                      <div style={{ marginTop: 3, fontFamily: 'monospace', fontSize: 10, color: '#7F77DD' }}>{local.target.primary}</div>
                    </div>
                  )}
                </Field>
              ) : (
                <>
                  <Field label="Selector primario" error={errors.primary}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        value={local.target.primary}
                        onChange={e => setLocal(p => ({ ...p, target: { ...p.target, primary: e.target.value } }))}
                        placeholder="#id, .class, [data-testid='x']"
                        style={INPUT_STYLE}
                      />
                      <select
                        value={local.target.strategy}
                        onChange={e => setLocal(p => ({ ...p, target: { ...p.target, strategy: e.target.value as SelectorStrategy } }))}
                        style={{ ...INPUT_STYLE, width: 100, flex: 'none' }}>
                        {STRATEGIES.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                  </Field>

                  <Field label="Selectores alternativos (fallbacks)">
                    {(local.target.fallbacks.length > 0 || true) && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {local.target.fallbacks.map((fb, i) => (
                          <div key={i} style={{ display: 'flex', gap: 6 }}>
                            <input
                              value={fb}
                              onChange={e => {
                                const next = [...local.target.fallbacks];
                                next[i] = e.target.value;
                                setLocal(p => ({ ...p, target: { ...p.target, fallbacks: next } }));
                              }}
                              placeholder={`Fallback ${i + 1}`}
                              style={INPUT_STYLE}
                            />
                            <button
                              onClick={() => setLocal(p => ({
                                ...p, target: {
                                  ...p.target,
                                  fallbacks: p.target.fallbacks.filter((_, j) => j !== i),
                                }
                              }))}
                              style={{ ...ICON_BTN, color: 'var(--color-text-danger)' }}
                            >✕</button>
                          </div>
                        ))}
                        <button
                          onClick={() => setLocal(p => ({ ...p, target: { ...p.target, fallbacks: [...p.target.fallbacks, ''] } }))}
                          style={ADD_BTN}
                        >
                          + Añadir fallback
                        </button>
                      </div>
                    )}
                  </Field>
                </>
              )}

              <Field label="Al fallar">
                <div style={{ display: 'flex', gap: 8 }}>
                  {ON_FAILURE_OPTIONS.map(opt => (
                    <button
                      key={opt}
                      onClick={() => setLocal(p => ({ ...p, on_failure: opt }))}
                      style={{
                        flex: 1, padding: '6px', borderRadius: 8,
                        border: `1px solid ${local.on_failure === opt ? 'var(--accent)' : 'var(--color-border-secondary)'}`,
                        background: local.on_failure === opt ? 'var(--accent-soft)' : 'transparent',
                        color: local.on_failure === opt ? 'var(--accent)' : 'var(--color-text-secondary)',
                        fontSize: 12, cursor: 'pointer', fontWeight: 500,
                        transition: 'all 0.15s',
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Timeout (ms)">
                <input
                  type="number"
                  value={(params.timeout_ms as number) ?? 5000}
                  onChange={e => updateParam('timeout_ms', Number(e.target.value))}
                  style={{ ...INPUT_STYLE, width: 120 }}
                />
              </Field>
            </div>
          )}

          {activeTab === 'params' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {paramDefs.length === 0 ? (
                <p style={{ color: 'var(--color-text-tertiary)', fontSize: 13, textAlign: 'center', marginTop: 32 }}>
                  Esta acción no requiere parámetros adicionales.
                </p>
              ) : paramDefs.map(def => (
                <Field key={def.key} label={def.label} error={errors[def.key]}>
                  {/* extract_to_column: select de columnas si hay dataset, sino text libre */}
                  {def.key === 'extract_to_column' && datasetColumns && datasetColumns.length > 0 ? (
                    <select
                      value={(params[def.key] as string) ?? ''}
                      onChange={e => updateParam(def.key, e.target.value)}
                      style={INPUT_STYLE}
                    >
                      <option value="">— Sin mapeo a dataset —</option>
                      {datasetColumns.map(col => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  ) : def.type === 'select' ? (
                    <select
                      value={(params[def.key] as string) ?? ''}
                      onChange={e => updateParam(def.key, e.target.value)}
                      style={INPUT_STYLE}
                    >
                      {(SELECT_OPTIONS[def.key] ?? []).map(opt => (
                        <option key={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : def.type === 'textarea' ? (
                    <textarea
                      value={(params[def.key] as string) ?? ''}
                      onChange={e => updateParam(def.key, e.target.value)}
                      placeholder={def.placeholder}
                      rows={4}
                      style={{ ...INPUT_STYLE, resize: 'vertical', fontFamily: 'monospace' }}
                    />
                  ) : def.type === 'checkbox' ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={(params[def.key] as boolean) ?? false}
                        onChange={e => updateParam(def.key, e.target.checked)}
                      />
                      Activar
                    </label>
                  ) : (
                    <input
                      type={def.type}
                      value={(params[def.key] as string | number) ?? ''}
                      onChange={e => updateParam(def.key,
                        def.type === 'number' ? Number(e.target.value) : e.target.value
                      )}
                      placeholder={def.placeholder}
                      style={INPUT_STYLE}
                    />
                  )}
                </Field>
              ))}
              {/* Nota informativa cuando UI_EXTRACT no tiene dataset */}
              {local.type === 'UI_EXTRACT' && (!datasetColumns || datasetColumns.length === 0) && (
                <div style={{ padding: '8px 12px', borderRadius: 8, background: '#F1EFE8', fontSize: 11, color: '#5F5E5A', border: '1px solid #DDD' }}>
                  El escenario no tiene dataset activo. El valor extraído solo aparecerá en la evidencia.
                </div>
              )}
            </div>
          )}

          {activeTab === 'meta' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Field label="Descripción">
                <textarea
                  value={local.meta.description}
                  onChange={e => setLocal(p => ({ ...p, meta: { ...p.meta, description: e.target.value } }))}
                  placeholder="Descripción de la acción para el reporte"
                  rows={3}
                  style={{ ...INPUT_STYLE, resize: 'vertical' }}
                />
              </Field>
              <Field label="Creado por">
                <div style={{ display: 'flex', gap: 8 }}>
                  {(['human', 'ai'] as const).map(opt => (
                    <button
                      key={opt}
                      onClick={() => setLocal(p => ({ ...p, meta: { ...p.meta, created_by: opt } }))}
                      style={{
                        flex: 1, padding: '6px', borderRadius: 8,
                        border: `1px solid ${local.meta.created_by === opt ? 'var(--accent)' : 'var(--color-border-secondary)'}`,
                        background: local.meta.created_by === opt ? 'var(--accent-soft)' : 'transparent',
                        color: local.meta.created_by === opt ? 'var(--accent)' : 'var(--color-text-secondary)',
                        fontSize: 12, cursor: 'pointer', fontWeight: 500,
                      }}
                    >
                      {opt === 'ai' ? '✦ IA' : '👤 Humano'}
                    </button>
                  ))}
                </div>
              </Field>
              {local.meta.healing_history?.length > 0 && (
                <Field label="Historial self-healing">
                  {local.meta.healing_history.map((h, i) => (
                    <div key={i} style={{
                      padding: 10, borderRadius: 8,
                      background: '#FAEEDA44', border: '1px solid #FAEEDA',
                      fontSize: 11, marginBottom: 6,
                    }}>
                      <div style={{ color: '#854F0B', fontWeight: 600 }}>Intento {i + 1}</div>
                      <div><span style={{ color: 'var(--color-text-tertiary)' }}>Original:</span> <code>{h.original_selector}</code></div>
                      <div><span style={{ color: 'var(--color-text-tertiary)' }}>Curado:</span> <code>{h.healed_selector}</code></div>
                      <div><span style={{ color: 'var(--color-text-tertiary)' }}>Confianza:</span> {(h.confidence * 100).toFixed(0)}%</div>
                    </div>
                  ))}
                </Field>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid var(--color-border-tertiary)',
          display: 'flex', gap: 8, justifyContent: 'flex-end',
        }}>
          <button onClick={onClose} style={{
            padding: '8px 16px', borderRadius: 8,
            border: '1px solid var(--color-border-secondary)',
            background: 'transparent', cursor: 'pointer',
            fontSize: 13, color: 'var(--color-text-secondary)',
          }}>
            Cancelar
          </button>
          <button onClick={handleSave} style={{
            padding: '8px 20px', borderRadius: 8,
            border: 'none', background: 'var(--accent)',
            color: '#fff', cursor: 'pointer',
            fontSize: 13, fontWeight: 600,
          }}>
            Guardar acción
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function Field({ label, children, error }: {
  label: string;
  children: React.ReactNode;
  error?: string;
}) {
  return (
    <div>
      <label style={{
        display: 'block', marginBottom: 6,
        fontSize: 11, fontWeight: 600, letterSpacing: '0.05em',
        color: 'var(--color-text-secondary)',
        textTransform: 'uppercase',
      }}>
        {label}
      </label>
      {children}
      {error && (
        <p style={{ marginTop: 4, fontSize: 11, color: 'var(--color-text-danger)' }}>{error}</p>
      )}
    </div>
  );
}

const INPUT_STYLE: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 8,
  border: '1px solid var(--color-border-secondary)',
  background: 'var(--color-background-secondary)',
  color: 'var(--color-text-primary)',
  fontSize: 13, boxSizing: 'border-box',
  outline: 'none',
};

const ICON_BTN: React.CSSProperties = {
  padding: '0 8px', border: 'none', background: 'transparent',
  cursor: 'pointer', fontSize: 14,
};

const ADD_BTN: React.CSSProperties = {
  padding: '6px 12px', borderRadius: 8, width: '100%',
  border: '1px dashed var(--color-border-secondary)',
  background: 'transparent', cursor: 'pointer',
  color: 'var(--color-text-tertiary)', fontSize: 12,
};